import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { LogOut, User, Shield, UserCheck, Lock, ChevronDown, Store, Gift, CalendarCheck, LayoutDashboard } from 'lucide-react'
import Icon from 'components/_UI/Icon'
import Text from 'components/_UI/Text'
import Loader from 'components/_UI/Loader'
import DynamicProfileImage from 'components/commons/DynamicProfileImage'
import { useUser } from 'context/user/User'
import { useAuth } from 'context/auth/Auth'
import { useOnSelectMenu } from 'context/general/OnSelectMenu'
import userApi from 'api/requests/user'
import ROLES from 'helpers/roles'
import useFeatureFlag from 'hooks/useFeatureFlag'
import { FEATURES } from 'constants/features'
import paths from 'routes/paths'
import colors from 'theme/colors'
import breakpoints from 'theme/breakpoints'
import indexPriority from 'theme/z-index'
import { ui } from 'theme/discover'
import { Row } from 'styled/alignment/Row'
import { Column } from 'styled/alignment/Column'
import useProducerPanelEntry from 'modules/dashboard/producer-access/useProducerPanelEntry'
import { openEventsPanel } from 'modules/dashboard/producer-access/enterProducerPanel'
import { actorHasOwnPanel } from 'helpers/dashboardAccess';

/**
 * Avatar + dropdown do usuário logado, compartilhado entre o HomeHeader e o Topbar.
 * O estado de abertura fica no header que o renderiza, porque o hambúrguer do
 * HomeHeader também alterna esse dropdown.
 */
export default function ProfileMenu({ visible = false, isOpen, onOpenChange }) {
    const navigate = useNavigate()
    const { user, setUser } = useUser()
    const { isAuth } = useAuth()
    const { setOnSelectMenu } = useOnSelectMenu()
    const { hasAccess: hasProducerPanelAccess, openPanel: openProducerPanel, picker: producerPanelPicker } = useProducerPanelEntry()

    const [isLoading, setIsLoading] = useState(false)
    const { enabled: reservationsEnabled } = useFeatureFlag(FEATURES.TABLE_RESERVATIONS)
    const [imageProfile, setImageProfile] = useState('')
    const [nameProfile, setNameProfile] = useState('')

    const dropdownItems = [
        {
            name: 'establishment',
            label: 'Estabelecimento',
            icon: <Store size='16px' color={colors.primaryDashboard} />,
            visible: user?.role?.id == ROLES.ESTABLISHMENT,
            onClick: () => navigate(paths.dashboard.home),
        },
        {
            name: 'event-panel',
            label: 'Painel de eventos',
            icon: <LayoutDashboard size='16px' color={colors.primaryDashboard} />,
            // Convidado para eventos, sem painel próprio nem de equipe: é o painel dele.
            visible: user?.is_event_collaborator && !visible && !hasProducerPanelAccess && !actorHasOwnPanel(user),
            onClick: openEventsPanel,
        },
        {
            name: 'producer-panel',
            // Quem já tem painel próprio entra aqui nos painéis de equipe.
            label: actorHasOwnPanel(user) ? 'Painéis de equipe' : 'Painel do produtor',
            icon: <LayoutDashboard size='16px' color={colors.primaryDashboard} />,
            visible: hasProducerPanelAccess && !visible,
            onClick: openProducerPanel,
        },
        {
            name: 'profile',
            label: user?.role?.id == ROLES.PRODUCER ? 'Resumo' : 'Estabelecimento' && !visible ? 'Perfil' : 'Estabelecimento',
            icon: <User size='16px' color={colors.primaryDashboard} />,
            visible: user?.role?.id == ROLES.ADMIN || user?.role?.id == ROLES.USER || visible,
            onClick: () => {
                if (user?.role?.id == ROLES.ADMIN) return navigate(paths.dashboard.statistics);
                if (user?.role?.id == ROLES.PRODUCER) return navigate(paths.dashboard.statistics);
                navigate(paths.user.profile);
            },
        },
        {
            name: 'reservations',
            label: 'Minhas reservas',
            icon: <CalendarCheck size='16px' color={colors.primaryDashboard} />,
            visible: reservationsEnabled,
            onClick: () => navigate(paths.user.reservations),
        },
        {
            name: 'affiliate',
            label: 'Indique e ganhe',
            icon: <Gift size='16px' color={colors.primaryDashboard} />,
            visible: true,
            onClick: () => {
                if (user?.role?.id == ROLES.PRODUCER) return navigate(paths.dashboard.affiliate);
                if (user?.role?.id == ROLES.ESTABLISHMENT || user?.role?.id == ROLES.ADMIN) return navigate(paths.dashboard.establishment.affiliate);
                navigate(paths.user.affiliate);
            },
        },
        {
            name: 'change-password',
            label: 'Alterar senha',
            icon: <Lock size='16px' color={colors.primaryDashboard} />,
            visible: true,
        },
        {
            name: 'use-terms',
            label: 'Termos de uso',
            icon: <UserCheck size='16px' color={colors.primaryDashboard} />,
            visible: true,
        },
        {
            name: 'privacy-policies',
            label: 'Política de privacidade',
            icon: <Shield size='16px' color={colors.primaryDashboard} />,
            visible: true,
        },
        {
            name: 'logout',
            label: 'Sair',
            icon: <LogOut size='16px' color={colors.primaryDashboard} />,
            visible: true,
        },
    ]

    useEffect(async () => {
        let currentUser = user

        if ((!currentUser || !currentUser?.id) && isAuth) {
            currentUser = await getUser()
        }

        handleProfileImage(currentUser)
    }, [])

    useEffect(async () => {
        handleProfileImage(user)
    }, [user])

    useEffect(() => {
        if (!isOpen) return;

        function handleClickOutside() {
            onOpenChange(false);
        }

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isOpen]);

    async function handleProfileImage(updatedUser) {
        let image = updatedUser?.avatar
        let name = updatedUser?.first_name

        if (updatedUser?.role?.id == ROLES.ESTABLISHMENT) {
            const currentEstablishment = updatedUser?.establishments[0]

            image = currentEstablishment?.profile_image_id?.thumb || image
            name = currentEstablishment?.name || name
        }

        setImageProfile(image)
        setNameProfile(name)
    }

    async function getUser() {
        setIsLoading(true);

        try {
            const { data } = await userApi.me();

            setUser(data);

            return data;
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    function toggleDropdown(e) {
        e.stopPropagation();
        onOpenChange(!isOpen);
    }

    return (
        <MenuRoot>
            {
                !user && isLoading ? <Loader /> : (
                    <>
                        <ContentProfile dropdown={isOpen} onClick={toggleDropdown}>
                            {
                                !isLoading && user ? (
                                    <StylePreview desktop>
                                        <DynamicProfileImage
                                            word={nameProfile}
                                            img={imageProfile}
                                            size={'36px'}
                                            variant='rounded'
                                        />
                                        <ProfileChevron open={isOpen}>
                                            <ChevronDown size={16} color={colors.night} />
                                        </ProfileChevron>
                                    </StylePreview>
                                ) : (
                                    <Loader />
                                )
                            }
                        </ContentProfile>

                        <StylePreview mobile dropdown={isOpen} onClick={toggleDropdown}>
                            <DynamicProfileImage
                                word={nameProfile}
                                img={imageProfile}
                                size={'40px'}
                                variant='rounded'
                            />
                        </StylePreview>

                        <Dropdown dropdown={isOpen}>
                            {dropdownItems?.map((el, index) => {
                                if (el.visible) {
                                    return (
                                        <Items key={index} onClick={el.onClick ?? (() => setOnSelectMenu({ tab: el?.name }))}>
                                            <ContainerItems>
                                                {el.icon}
                                                <Text paddingBottom={'0px'} ellipsisAt={'95%'} color={colors.primaryDashboard}>
                                                    {el.label}
                                                </Text>
                                            </ContainerItems>
                                        </Items>
                                    )
                                }
                            })}
                        </Dropdown>
                    </>
                )
            }
            {producerPanelPicker}
        </MenuRoot>
    )
}

const MenuRoot = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
`;

const ContentProfile = styled.div`
    display: none;
    cursor: pointer;
    align-items: center;
    transition: all ease-in-out .25s;

    &:hover {
        opacity: 0.85;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        background: ${colors.neutralLight};
        width: auto;
        display: flex;
        padding: 3px 10px 3px 3px;
        gap: 6px;
        border-radius: 999px;
        border: 1px solid ${ui.border};
        transition: border-color 0.15s ease, box-shadow 0.15s ease;

        &:hover {
            opacity: 1;
            border-color: #DCD8E8;
            box-shadow: ${ui.cardShadow};
        }
    }
`;

const ProfileChevron = styled.div`
    display: flex;
    align-items: center;
    transition: transform .2s ease;
    ${({ open }) => open && `transform: rotate(180deg);`}
`;

const StylePreview = styled(Row)`
    ${({ mobile }) => mobile && `
        justify-content: flex-end;
        @media screen and (min-width: ${breakpoints.lg}) {
            display: none;
        }
    `}
    ${({ desktop }) => desktop && `
         display: none;
        @media screen and (min-width: ${breakpoints.lg}) {
            display: flex;
            align-items: center;
            & > p {
                padding-bottom: 0px;
            }
            justify-content: space-around;
        }
    `}
`;

const Dropdown = styled(Column)`
    position: fixed;
    top: 70px;
    right: 16px;
    width: 220px;
    border-radius: 18px;
    background: ${colors.neutralLight};
    border: 1px solid ${ui.border};
    box-shadow: 0 16px 40px rgba(21, 19, 42, 0.14), 0 2px 8px rgba(21, 19, 42, 0.06);
    padding: 6px 0;
    max-height: 350px;
    overflow-y: auto;
    opacity: 0;
    transform: translateY(8px);
    pointer-events: none;
    transition: all .2s ease;
    z-index: ${indexPriority.tremendous};

    ${({ dropdown }) => dropdown === true && `
        opacity: 1;
        transform: translateY(0);
        pointer-events: auto;
    `}
`;

const Items = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 10px 12px;
    height: auto;
    width: auto;
    margin: 0 6px;
    border-radius: 12px;
    cursor: pointer;
    transition: background .15s ease;
    &:hover {
        background: ${ui.tagBg};
        p {
            color: ${colors.occasionalPurple} !important;
        }
        & > div > svg {
            stroke: ${colors.occasionalPurple}
        }
        & > div > div > svg {
            fill: ${colors.occasionalPurple}
        }
    }
`;

const ContainerItems = styled(Row)`
    justify-content: flex-start;
    padding: 0 0 5px 10px;
    align-items: flex-end;
    & > p  {
        padding-bottom: 0;
        margin-left: 6px
    }
`;

const ContentIcon = styled.div`
    width: 20px;
    height: 20px;
    margin-right: 6px;

    ${({ wallet }) => wallet && `
        width: 20px;
        height: 20px;
        margin-right: 0px;
    `}
`;
